/** Instance-owned generation extracted from the separately maintained Steam Punk game.
 * The host supplies clock, randomness, DOM and lifecycle; no globals or score API are shared. */
export function installSteamGeneration(runtime) {
  runtime.buildOpeningBoard = function () {
    const rows = runtime.currentModeConfig().openingRows || 12;
    for (let i = 0; i < rows; i += 1) {
      runtime.spawnWave(false);
      if (i < rows - 1) {
        runtime.shiftBoardEntities(1);
      }
    }
    runtime.markBoardSceneDirty();
  };
  runtime.shiftBoardEntities = function (rowCount = 1, options = {}) {
    const distance = runtime.ROW_STEP * Math.max(1, Math.floor(rowCount || 1));
    runtime.game.bricks.forEach((brick) => {
      brick.y += distance;
    });
    runtime.game.orbs.forEach((orb) => {
      orb.y += distance;
    });
    runtime.game.walls.forEach((wall) => {
      wall.y += distance;
      wall.cy += distance;
    });
    runtime.game.planets.forEach((planet) => {
      planet.y += distance;
    });
    runtime.game.spatialDirty = true;
    runtime.markBoardSceneDirty(options);
  };
  runtime.spawnWave = function (advance = true) {
    const refillRows = advance
      ? Math.max(0, Math.floor(runtime.game.boardClearRefillRows || 0))
      : 0;
    const rowsToSpawn = Math.max(1, refillRows || 1);
    const suppressRefillStars = refillRows > 0;
    if (advance) runtime.game.boardClearRefillRows = 0;
    if (advance) {
      runtime.game.level += 1;
    }
    const cols = runtime.GRID_COLS;
    const gap = runtime.GRID_GAP;
    const cell = (runtime.W - runtime.GRID_MARGIN * 2 - gap * (cols - 1)) / cols;
    const brickSize = Math.min(cell, runtime.BRICK_H);
    const y = 58;
    let createdBricks = 0;
    for (let rowIndex = 0; rowIndex < rowsToSpawn; rowIndex += 1) {
      if (advance || rowIndex > 0) {
        runtime.shiftBoardEntities(1, { shiftCue: advance && rowIndex === 0 });
      }
      const density = runtime.clamp(0.58 + runtime.game.level * 0.008, 0.6, 0.84);
      const rowPattern = Math.floor(runtime.rand(0, 6));
      const laneA = Math.floor(runtime.rand(1, cols - 1));
      const laneB = runtime.clamp(laneA + (runtime.random() < 0.5 ? 2 : -2), 1, cols - 2);
      for (let col = 0; col < cols; col += 1) {
        const laneGap =
          (rowPattern === 1 && col === laneA) ||
          (rowPattern === 2 && (col === laneA || col === laneB)) ||
          (rowPattern === 3 && col % 6 === laneA % 6) ||
          (rowPattern === 4 && col === Math.floor(cols / 2)) ||
          (rowPattern === 5 && (col === 0 || col === cols - 1) && runtime.random() < 0.55);
        const patterned = !laneGap && (runtime.random() < density || col === 0 || col === cols - 1);
        if (!patterned) continue;
        const hp = Math.ceil(
          runtime.game.level * runtime.rand(0.82, 1.5) + col * 0.14 + rowPattern * 0.28,
        );
        runtime.game.bricks.push({
          x: runtime.GRID_MARGIN + col * (cell + gap),
          y,
          w: brickSize,
          h: brickSize,
          hp,
          maxHp: hp,
          heat: 0,
          dead: false,
        });
        createdBricks += 1;
      }
      runtime.game.spatialDirty = true;
      runtime.markBoardSceneDirty();
      if (
        runtime.random() <
        runtime.clamp(
          0.34 - runtime.game.level * 0.006 + runtime.game.powerupChanceBonus,
          0.18,
          0.54,
        )
      ) {
        runtime.spawnPowerupOrb(runtime.rand(52, runtime.W - 52), y + 18, runtime.pickOrbType());
      }
      runtime.spawnTunnelPattern(y);
      if (!suppressRefillStars) runtime.spawnPlanet(y);
    }
    if (runtime.game.bricks.some((brick) => !brick.dead)) runtime.game.boardClearRewarded = false;
    if (
      runtime.currentModeConfig().endOnDangerRail &&
      runtime.game.bricks.some((brick) => !brick.dead && brick.y + brick.h >= runtime.dangerY)
    ) {
      runtime.game.status = 'gameover';
      runtime.prepareGameOverScoreboard();
      runtime.showOverlay(
        'Game Over',
        'The maze hit the rail',
        'Upgrade earlier, bank ricochets through the rails, and keep the volley moving.',
      );
    }
    return createdBricks;
  };
  runtime.spawnTunnelPattern = function (rowY) {
    const chance = runtime.clamp(0.22 - runtime.game.level * 0.003, 0.1, 0.22);
    if (runtime.random() > chance) return;
    const pattern =
      runtime.game.level < 4 ? Math.floor(runtime.rand(0, 2)) : Math.floor(runtime.rand(0, 5));
    const baseY = rowY + 38;
    const life = 7 + Math.floor(runtime.game.level / 3);
    const addWall = (x, y, w, h, angle = 0, boost = 1) => {
      const hp = Math.ceil(life * boost);
      runtime.game.walls.push(runtime.createWallBody(x, y, w, h, angle, hp, true));
    };
    if (pattern === 0) {
      const x = runtime.rand(92, 190);
      addWall(x, baseY, 280, 8, -0.08, 1.2);
      addWall(x + 38, baseY + 34, 280, 8, 0.08, 1.2);
      addWall(x + 4, baseY + 68, 250, 8, -0.06, 1.1);
    } else if (pattern === 1) {
      const cx = runtime.rand(250, 390);
      addWall(cx - 150, baseY + 8, 160, 8, 0.5, 1.2);
      addWall(cx + 8, baseY + 8, 160, 8, -0.5, 1.2);
      addWall(cx - 130, baseY + 56, 130, 8, -0.36, 1);
      addWall(cx + 16, baseY + 56, 130, 8, 0.36, 1);
    } else if (pattern === 2) {
      const left = runtime.rand(70, 130);
      addWall(left, baseY + 12, 10, 82, 0, 1.4);
      addWall(left + 320, baseY + 12, 10, 82, 0, 1.4);
      addWall(left + 48, baseY + 6, 250, 8, 0.04, 1.1);
      addWall(left + 48, baseY + 90, 250, 8, -0.04, 1.1);
    } else if (pattern === 3) {
      addWall(78, baseY + 10, 160, 8, 0.24, 1);
      addWall(440, baseY + 26, 160, 8, -0.24, 1);
      addWall(78, baseY + 52, 160, 8, -0.24, 1);
      addWall(440, baseY + 68, 160, 8, 0.24, 1);
    } else {
      const gate = runtime.rand(250, 390);
      addWall(58, baseY + 18, gate - 92, 8, 0.18, 1.1);
      addWall(gate + 70, baseY + 18, runtime.W - gate - 128, 8, -0.18, 1.1);
      addWall(58, baseY + 68, gate - 66, 8, -0.18, 1.1);
      addWall(gate + 44, baseY + 68, runtime.W - gate - 102, 8, 0.18, 1.1);
      addWall(gate - 12, baseY + 32, 10, 48, 0, 1.3);
    }
  };
  runtime.createWallBody = function (x, y, w, h, angle, hp, tunnel) {
    return {
      x,
      y,
      w,
      h,
      angle,
      cos: Math.cos(angle),
      sin: Math.sin(angle),
      negCos: Math.cos(-angle),
      negSin: Math.sin(-angle),
      cx: x + w / 2,
      cy: y + h / 2,
      hx: w / 2,
      hy: h / 2,
      hp,
      maxHp: hp,
      heat: 0,
      tunnel,
    };
  };
  runtime.buildGravityLut = function (planet) {
    const lut = [];
    const influenceSq = planet.influence * planet.influence;
    const coreSq = planet.coreSq;
    const invRange = 1 / Math.max(1, planet.influence - planet.r);
    for (let i = 0; i < runtime.GRAVITY_LUT_SIZE; i += 1) {
      const t = (i + 0.5) / runtime.GRAVITY_LUT_SIZE;
      const distSq = Math.max(coreSq, t * influenceSq);
      const dist = Math.sqrt(distSq);
      const proximity = runtime.clamp(1 - (dist - planet.r) * invRange, 0, 1);
      const falloff = proximity * proximity;
      const coreBoost = 1 + proximity * proximity * 6;
      const force = (planet.strength / distSq) * falloff * coreBoost;
      lut.push({
        invDist: 1 / dist,
        force,
        tangent: proximity > 0.42 ? force * 0.28 : 0,
        glow: proximity * 0.85,
        proximity,
      });
    }
    return lut;
  };
  runtime.spawnPlanet = function (rowY) {
    const chance = runtime.clamp(0.18 + runtime.game.level * 0.006, 0.18, 0.34);
    if (runtime.random() > chance) return;
    const highestHp = Math.max(
      1,
      ...runtime.game.bricks.filter((brick) => !brick.dead).map((brick) => brick.hp),
    );
    const mass = highestHp * 100;
    const strength = mass * runtime.rand(6200, 9800);
    const radius = runtime.rand(22, 34);
    const planet = {
      id: runtime.game.nextPlanetId,
      x: runtime.rand(80, runtime.W - 80),
      y: rowY + runtime.rand(32, 92),
      r: radius,
      mass,
      strength,
      hits: 0,
      maxHits: 100,
      influence: runtime.rand(180, 235),
      coreSq: (radius + 4) ** 2,
      influenceSq: 0,
      invInfluenceSq: 0,
      gravityLut: null,
      spin: runtime.rand(0, Math.PI * 2),
      hue: runtime.rand(180, 230),
    };
    planet.influenceSq = planet.influence * planet.influence;
    planet.invInfluenceSq = 1 / planet.influenceSq;
    planet.gravityLut = runtime.buildGravityLut(planet);
    runtime.game.planets.push(planet);
    runtime.game.nextPlanetId += 1;
  };
  runtime.pickOrbType = function () {
    if (runtime.random() < 0.09) {
      const specials = ['clear5', 'clear10', 'halve', 'chaos'];
      return specials[Math.floor(runtime.rand(0, specials.length))];
    }
    const roll = runtime.random();
    if (roll < 0.38) return 'ball';
    if (roll < 0.55) return 'split';
    if (roll < 0.9) return 'coin';
    return 'blast';
  };
  runtime.spawnPowerupOrb = function (x, y, type = runtime.pickOrbType()) {
    runtime.game.orbs.push({
      x: runtime.clamp(x, 52, runtime.W - 52),
      y: runtime.clamp(y, 72, runtime.H - 210),
      r: 9,
      type,
      pulse: runtime.rand(0, Math.PI * 2),
    });
  };
  runtime.spawnNeutronRewardPowerups = function (planet) {
    if (runtime.game.mode !== 'speed' && runtime.game.mode !== 'timed') return;
    for (let i = 0; i < runtime.NEUTRON_REWARD_POWERUPS; i += 1) {
      const angle = (Math.PI * 2 * i) / runtime.NEUTRON_REWARD_POWERUPS + runtime.rand(-0.35, 0.35);
      const distance = runtime.rand(44, 86);
      runtime.spawnPowerupOrb(
        planet.x + Math.cos(angle) * distance,
        planet.y + Math.sin(angle) * distance,
        runtime.pickOrbType(),
      );
    }
    runtime.game.mods.unshift(`Neutron reward: +${runtime.NEUTRON_REWARD_POWERUPS} powerups`);
    runtime.game.mods.length = 4;
  };
  runtime.awardNeutronFuel = function (planet) {
    runtime.game.neutronFuel += 1;
    const ready = runtime.game.neutronFuel >= runtime.game.neutronFuelCost;
    runtime.game.mods.unshift(
      ready
        ? 'Neutron fuel charged'
        : `Neutron fuel +1 (${runtime.game.neutronFuel}/${runtime.game.neutronFuelCost})`,
    );
    runtime.game.mods.length = 4;
    runtime.addFloater(
      planet.x,
      planet.y - planet.r - 20,
      '+NEUTRON FUEL',
      ready ? '#fff8f0' : '#9de7d3',
      {
        scale: ready ? 1.45 : 1.15,
        life: 1.35,
        stroke: 'rgba(8, 22, 32, 0.92)',
        shadowBlur: ready ? 34 : 20,
      },
    );
  };
}
